import type { Domain } from "@amzn/d16-g-cdb-publisher-portal-typescript-client";
import { Card, CardHeader, Text, Code, Heading } from "@amzn/storm-ui";
import { useAsync, useMountEffect, useUnmountEffect } from "@react-hookz/web";
import React from "react";
import { useParams } from "react-router";
import styled from "styled-components";
import { useStore } from "zustand";

import { getPublisherPortalApiClient } from "../../api/publisherPortalApi/publisherPortalApi";
import { ActionBar } from "../../components/createPackagedInsight/ActionBar";
import {
    SettingsCard,
    TemplatesCard,
} from "../../components/createPackagedInsight/cards";
import { useAsyncErrorStatusNotification } from "../../hooks/notifications/useAsyncErrorStatusNotification";
import { ctiStore } from "../../store/cti";
import { isLoading } from "../../utils/api.utils";
import { useGetComponentHeights } from "../../utils/app.utils";

const CardContainer = styled.div`
    display: block;
    margin: 50px auto;
    max-width: 900px;
`;

const ColumnFlex = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const FIXED_CONTAINERS = ["insighthub-alert"];

/**
 * @deprecated
 */
export const CreatePackagedInsightContainer: React.FunctionComponent = () => {
    const { domainId: domainIdentifier = "" } = useParams();
    const [templateIdentifier, setTemplateIdentifier] =
        React.useState<string>("");
    const [setCti, resetCti] = useStore(ctiStore, (state) => [
        state.setCti,
        state.resetCti,
    ]);

    // used to get how much height to subtract from total view port
    const minus = useGetComponentHeights(FIXED_CONTAINERS);

    const [domainState, domainAction] = useAsync(async (): Promise<Domain> => {
        const { data } = await (
            await getPublisherPortalApiClient()
        ).getDomain({
            domainIdentifier,
        });
        if (data?.cti) setCti(data.cti);
        return data!;
    });
    useAsyncErrorStatusNotification(domainState);

    useMountEffect(async () => {
        void domainAction.execute();
    });

    useUnmountEffect(() => {
        resetCti();
    });

    const dimensionSections: any = React.useMemo(() => {
        return domainState.result?.dimensions;
    }, [domainState.result]);

    return (
        <section>
            <ActionBar
                domainIdentifier={domainIdentifier}
                templateIdentifier={templateIdentifier}
            />
            <CardContainer>
                <ColumnFlex>
                    {domainState.status === "success" &&
                    !domainState.result?.enabled ? (
                        <Card>
                            <CardHeader>
                                <Heading renderAs="h4">
                                    Domain has been disabled
                                </Heading>
                                <Text renderAs="p">
                                    Please contact content owner for more
                                    details
                                </Text>
                            </CardHeader>
                            <Heading renderAs="h4">
                                Domain: {domainState.result?.name}
                            </Heading>
                            <Code renderAs="code">
                                DomainId: {domainState.result?.id}
                            </Code>
                        </Card>
                    ) : (
                        <>
                            <SettingsCard
                                dimensionSections={dimensionSections}
                                loading={isLoading(domainState.status)}
                            />
                            <TemplatesCard
                                domainIdentifier={domainIdentifier}
                                templateIdentifier={templateIdentifier}
                                setTemplateIdentifier={setTemplateIdentifier}
                            />
                        </>
                    )}
                </ColumnFlex>
            </CardContainer>
        </section>
    );
};
